import type { Bitmap } from "./types.ts";

function createGaussianKernel(radius: number) {
	const sigma = Math.max(0.1, radius);
	const kernelSize = 2 * Math.ceil(sigma * 2.57) + 1;
	const kernel = new Float32Array(kernelSize);
	const halfSize = Math.floor(kernelSize / 2);
	let sum = 0;

	for (let i = 0; i < kernelSize; i++) {
		const x = i - halfSize;
		const value = Math.exp(-(x * x) / (2 * sigma * sigma));
		kernel[i] = value;
		sum += value;
	}

	for (let i = 0; i < kernelSize; i++) {
		kernel[i] /= sum;
	}
	return kernel;
}

function* gaussianBlur(
	{ data, width, height }: Bitmap,
	radius: number,
): Generator<undefined, Bitmap> {
	const tempPixels = new Float32Array(data.length);
	const outputPixels = new Uint8Array(data.length);

	const kernel = createGaussianKernel(radius);
	const kernelSize = kernel.length;
	const halfKernelSize = Math.floor(kernelSize / 2);

	// Horizontal pass
	for (let y = 0; y < height; y++) {
		for (let x = 0; x < width; x++) {
			let r = 0,
				g = 0,
				b = 0;
			for (let i = 0; i < kernelSize; i++) {
				const weight = kernel[i];
				const pixelX = Math.min(width - 1, Math.max(0, x - halfKernelSize + i));
				const srcIndex = (y * width + pixelX) * 4;
				r += data[srcIndex] * weight;
				g += data[srcIndex + 1] * weight;
				b += data[srcIndex + 2] * weight;
			}
			const destIndex = (y * width + x) * 4;
			tempPixels[destIndex] = r;
			tempPixels[destIndex + 1] = g;
			tempPixels[destIndex + 2] = b;
			tempPixels[destIndex + 3] = 0xff;
		}
		yield;
	}

	// Vertical pass
	for (let y = 0; y < height; y++) {
		for (let x = 0; x < width; x++) {
			let r = 0;
			let g = 0;
			let b = 0;
			for (let i = 0; i < kernelSize; i++) {
				const weight = kernel[i];
				const pixelY = Math.min(
					height - 1,
					Math.max(0, y - halfKernelSize + i),
				);
				const srcIndex = (pixelY * width + x) * 4;
				r += tempPixels[srcIndex] * weight;
				g += tempPixels[srcIndex + 1] * weight;
				b += tempPixels[srcIndex + 2] * weight;
			}
			const destIndex = (y * width + x) * 4;
			outputPixels[destIndex] = r;
			outputPixels[destIndex + 1] = g;
			outputPixels[destIndex + 2] = b;
			outputPixels[destIndex + 3] = 0xff;
		}
		yield;
	}

	return {
		data: outputPixels,
		width,
		height,
	};
}

export default gaussianBlur;
